// src/components/currencies/CurrenciesTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetCurrencies from "../../hooks/currencies/useGetCurrencies";
import useDeleteCurrency from "../../hooks/currencies/useDeleteCurrency";
import useDeleteAllCurrencies from "../../hooks/currencies/useDeleteAllCurrencies";
import useRestoreCurrency from "../../hooks/currencies/useRestoreCurrency";
import useRestoreAllCurrencies from "../../hooks/currencies/useRestoreAllCurrencies";
import useForceDeleteCurrency from "../../hooks/currencies/useForceDeleteCurrency";
import useForceDeleteAllCurrencies from "../../hooks/currencies/useForceDeleteAllCurrencies";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import CurrenciesTableRow from "./Table/CurrenciesTableRow";
import CurrenciesTableColumns from "./Table/CurrenciesTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const CurrenciesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("currencies_list"),
            create: has("currencies_create"),
            edit: has("currencies_edit"),
            delete: has("currencies_delete"),
            forceDelete: has("currencies_force_delete"),
            archiveList: has("currencies_archive_list"),
            restore: has("currencies_restore"),
            restoreAll: has("currencies_restore-all-table"),
            deleteAll: has("currencies_delete-all-table"),
            massForceDeleteAll: has("currencies_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/currencies/archive");

    // CRUD Hooks
    const { deleteCurrency, isDelete } = useDeleteCurrency();
    const { deleteAllCurrencies, isDeleteAll } = useDeleteAllCurrencies();
    const { restoreCurrency, isRestore } = useRestoreCurrency();
    const { restoreAllCurrencies, isRestoreAll } = useRestoreAllCurrencies();
    const { forceDeleteCurrency, isForceDelete } = useForceDeleteCurrency();
    const { forceDeleteAllCurrencies, isForceDeleteAll } = useForceDeleteAllCurrencies();

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/currencies",
        deleteSingle: deleteCurrency,
        deleteAll: deleteAllCurrencies,
        restoreSingle: restoreCurrency,
        restoreAll: restoreAllCurrencies,
        forceDeleteSingle: forceDeleteCurrency,
        forceDeleteAll: forceDeleteAllCurrencies,
        isArchive,
    });

    // Local State (specific to this table)
    const [statusFilter, setStatusFilter] = React.useState("");

    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, currencies, meta } = useGetCurrencies(page, perPage, filters);

    const items = useMemo(() => currencies?.items || [], [currencies]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <CurrenciesTableRow
            key={params.item.id}
            currency={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive ? !perms.massForceDeleteAll : !perms.deleteAll;

    const title = isArabic ? "العملات" : "Currencies";
    const subtitle = isArchive
        ? isArabic
            ? "عرض العملات المحذوفة (الأرشيف)"
            : "Archived currencies (soft deleted)"
        : isArabic
            ? "إدارة العملات المتاحة في النظام"
            : "Manage available currencies in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة عملة"
            : "Create new currency";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للاستعادة" : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllCurrencies({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للإنشاء" : "No create permission"
                );
                return;
            }

            navigate("/currencies/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/currencies/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/currencies"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={CurrenciesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={isArchive ? "لا توجد عملات في الأرشيف" : "لا توجد عملات"}
                emptyLabelEn={isArchive ? "No archived currencies" : "No currencies"}
                loadErrorAr="حدث خطأ أثناء تحميل العملات"
                loadErrorEn="Failed to load currencies"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default CurrenciesTable;
